import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import Form from 'components/form/Form';
import Modal from 'components/global/Modal';
import Tabs from 'components/global/Tabs';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { PRICE_CONFIGURATIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import ListingSettingsForm from './ListingSettingsForm';
import PriceSettingsForm from './PriceSettingsForm';

/**
 * @author Burak Ak
 *
 */

const OTODeliveryPriceForm = NiceModal.create(({ id, modalName, mode }) => {
	// Hooks and states declaration
	const { t } = useTranslation();
	const { Currency } = useLocale();
	const modal = useModal();
	const dispatch = useDispatch();
	const { closeModal } = useModalRouter();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const [errorTabs, setErrorTabs] = useState([]);
	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	// Validation schema definition
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					shippingContractID: yup.number().nullable().required(),
					serviceType: yup.string().nullable().required(),
					tempInsideWarehouse: yup
						.string()
						.nullable()
						.when('serviceType', {
							is: (val) => val === 'COLD_DELIVERY',
							then: (schema) => schema.required()
						}),
					tempInsideVehicle: yup
						.string()
						.nullable()
						.when('serviceType', {
							is: (val) => val === 'COLD_DELIVERY',
							then: (schema) => schema.required()
						}),
					shipmentType: yup.string().nullable().required(),
					maxOrderValue: yup.number().nullable().required(),
					maxCodValue: yup.number().nullable().required(),
					maxKm: yup.number().nullable(),
					maxWeight: yup.number().nullable(),
					maxDimension: yup.number().nullable(),
					maxPackageCount: yup.number().nullable(),
					isSameCity: yup.boolean().nullable(),
					isBetweenCity: yup.boolean().nullable(),
					insuranceFee: yup.number().nullable(),
					trackingType: yup.string().nullable(),
					pickupType: yup.string().nullable().required(),
					deliveryType: yup.string().nullable().required(),
					deliveryPromise: yup.string().nullable(),
					basePriceMethodType: yup.string().nullable().required(),
					maxFreeWeight: yup.number().nullable(),
					volumetricWeightRate: yup.number().nullable().required(),
					extraWeightPerKg: yup.string().nullable(),
					maxFreeKm: yup.number().nullable(),
					extraChargePerKm: yup.string().nullable(),
					applyCodRateAfter: yup.string().nullable(),
					codCharge: yup.string().nullable(),
					codPercentage: yup.number().nullable().max(100),
					returnPercentage: yup
						.number()
						.nullable()
						.min(0)
						.when('serviceType', {
							is: (val) => val !== 'IMPORT',
							then: (schema) => schema.required()
						}),
					discountPercentage: yup.number().nullable().min(0).max(100),
					firstPriceRate: yup.number().nullable().min(1).max(99.99),
					cardOnDeliveryPercentage: yup.number().nullable().max(100),
					cutOffTime: yup.string().nullable(),
					chargingStatus: yup.string().nullable(),
					countryAddress: yup.object().shape({
						countryName: yup.string().nullable().required()
					}),
					notice: yup.string().nullable(),
					expectedDropOffDescription: yup.string().nullable(),
					expectedDeliveryDescription: yup.string().nullable(),
					successMessage: yup.string().nullable(),
					customerWaitingTime: yup.number().nullable(),
					unsuccessfulReturnTime: yup.number().nullable(),
					checkAllBranches: yup.string().nullable(),
					liveTracking: yup.boolean().nullable(),
					doorstepDisposalFee: yup.number().nullable(),
					disposalFee: yup.number().nullable(),
					starterDiscountAmount: yup.number().nullable(),
					scaleDiscountAmount: yup.number().nullable(),
					starterDiscountPercentage: yup.number().nullable().min(1).max(100),
					scaleDiscountPercentage: yup.number().nullable().min(1).max(100),
					waitingTimePickup: yup.number().nullable(),
					fuelSurcharge: yup.number().nullable(),
					lockerIds: yup.array(yup.number()).nullable(),
					lockerPassword: yup.string().nullable()
				})
				.required(),
		[]
	);

	const fillForm = useCallback(async () => {
		if (!id) {
			return {
				shippingContractID: null,
				serviceType: null,
				tempInsideWarehouse: null,
				tempInsideVehicle: null,
				maxOrderValue: null,
				maxCodValue: null,
				shipmentType: 'BOTH_FORWARD_AND_REVERSE_SHIPMENT',
				maxKm: null,
				maxWeight: null,
				maxDimension: null,
				maxPackageCount: null,
				isSameCity: false,
				isBetweenCity: false,
				insuranceFee: null,
				trackingType: null,
				pickupType: null,
				deliveryType: null,
				deliveryPromise: null,
				basePriceMethodType: null,
				maxFreeWeight: null,
				volumetricWeightRate: 4000,
				extraWeightPerKg: null,
				maxFreeKm: null,
				extraChargePerKm: null,
				applyCodRateAfter: null,
				codCharge: null,
				codPercentage: null,
				returnPercentage: null,
				discountPercentage: null,
				cutOffTime: null,
				chargingStatus: null,
				country: null,
				notice: null,
				expectedDropOffDescription: null,
				expectedDeliveryDescription: null,
				successMessage: null,
				cardOnDeliveryPercentage: null,
				firstPriceRate: null,
				currency: Currency,
				customerWaitingTime: null,
				unsuccessfulReturnTime: null,
				checkAllBranches: null,
				liveTracking: false,
				doorstepDisposalFee: null,
				disposalFee: null,
				starterDiscountAmount: null,
				scaleDiscountAmount: null,
				starterDiscountPercentage: null,
				scaleDiscountPercentage: null,
				waitingTimePickup: null,
				lockerIds: [],
				lockerPassword: null
			};
		}
		return await axiosPrivate
			.post(PRICE_CONFIGURATIONS.GET_OTO_DELIVERY_PRICE, { id })
			.then((response) => {
				const { data, status } = response;

				if (status === 200) {
					return {
						...data,
						shippingContractID: data.shippingContract.id,
						currency: data.shippingContract.currency ?? undefined,
						countryAddress: {
							countryName: data.country ?? null
						}
					};
				} else {
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});
	}, []);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const onSubmit = (data) => {
		const url = PRICE_CONFIGURATIONS.ADD_OTO_DELIVERY_PRICE;
		axiosPrivate
			.post(url, {
				...data,
				id: id ?? null,
				shippingContract: {
					id: data.shippingContractID
				},
				country: data.countryAddress.countryName
			})
			.then((response) => {
				const { status } = response;

				if (status === 200) {
					dispatch(updateDynamicListData(DYNAMICLISTS.OTO_DELIVERY_PRICES));
					showSuccess();
					handleCloseModal();
				}
			})
			.catch((error) => {
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
				AppLogger.error(error);
			});
	};

	const tabs = useMemo(
		() => [
			{
				label: 'priceSettingsForm',
				component: () => <PriceSettingsForm />,
				fields: [
					'shippingContractID',
					'serviceType',
					'countryAddress',
					'volumetricWeightRate',
					'chargingStatus',
					'basePriceMethodType',
					'isSameCity',
					'isBetweenCity',
					'shipmentType',
					'maxOrderValue',
					'maxCodValue',
					'maxKm',
					'maxWeight',
					'maxDimension',
					'maxPackageCount',
					'maxFreeWeight',
					'extraWeightPerKg',
					'maxFreeKm',
					'extraChargePerKm',
					'applyCodRateAfter',
					'codCharge',
					'codPercentage',
					'returnPercentage',
					'discountPercentage',
					'firstPriceRate',
					'cardOnDeliveryPercentage',
					'cutOffTime',
					'chargingStatus',
					'basePriceMethodType',
					'maxFreeWeight',
					'extraWeightPerKg',
					'maxFreeKm',
					'extraChargePerKm',
					'applyCodRateAfter',
					'codCharge',
					'codPercentage',
					'cardOnDeliveryPercentage',
					'returnPercentage',
					'discountPercentage',
					'firstPriceRate',
					'starterDiscountAmount',
					'scaleDiscountAmount',
					'starterDiscountPercentage',
					'scaleDiscountPercentage',
					'lockerSizes',
					'fuelSurcharge'
				],
				error: errorTabs.includes('priceSettingsForm')
			},
			{
				label: 'listingSettingsForm',
				component: () => <ListingSettingsForm />,
				fields: [
					'pickupType',
					'deliveryType',
					'insuranceFee',
					'cutOffTime',
					'deliveryPromise',
					'trackingType',
					'notice',
					'expectedDeliveryDescription',
					'expectedDropOffDescription',
					'successMessage',
					'tempInsideWarehouse',
					'tempInsideVehicle',
					'customerWaitingTime',
					'unsuccessfulReturnTime',
					'checkAllBranches',
					'liveTracking',
					'doorstepDisposalFee',
					'disposalFee',
					'waitingTimePickup',
					'lockerPassword'
				],
				error: errorTabs.includes('listingSettingsForm')
			}
		],
		[errorTabs]
	);

	const onError = (errors) => {
		AppLogger.log(errors);
		const errorTabs = [];

		tabs.forEach((tab) => {
			if (tab.fields?.some((field) => errors[field])) {
				errorTabs.push(tab.label);
			}
		});
		if (errorTabs.length > 0) {
			showError({
				header: t('error.validationError'),
				message: t('error.tabsHaveErrors', {
					tabNames: errorTabs.map((tab) => t(tab)).join(', '),
					count: errorTabs.length
				})
			});
		}
		setErrorTabs(errorTabs);
	};

	return (
		<Modal fullwidth name={modalName} maxWidth="medium" className="modal-otoDeliveryPriceForm">
			<ModalTitle>
				<Typography variant="p1">otoDeliveryPriceForm</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<Tabs data={tabs} />
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={handleCloseModal} />

				<Button
					loading={isLoading}
					label={mode === 'edit' ? 'save' : 'button.add'}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					onClick={handleSubmit(onSubmit, onError)}
				/>
			</ModalActions>
		</Modal>
	);
});

export default OTODeliveryPriceForm;
